<template>
  <base-material-card
    color="primary"
    icon="book"
    inline
    class="px-5 py-3"
  >
    <template v-slot:after-heading>
      <div class="text-h2 font-weight-light">
        {{ $t('corrections') }}
      </div>
    </template>

    <v-row
      class="mb-5"
    >
      <v-col cols="2">
        <v-menu
          ref="datePicker"
          v-model="datePicker"
          :close-on-content-click="false"
          :return-value.sync="invoiceDate"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="invoiceDate"
              :label="$t('invoice_date')"
              prepend-icon="event"
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="invoiceDate"
            first-day-of-week="1"
            :max="invoiceDate"
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="$refs.datePicker.save(null)"
            >
              {{ $t('clear') }}
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="datePicker = false"
            >
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="$refs.datePicker.save(invoiceDate)"
            >
              {{ $t('ok') }}
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="2">
        <v-btn
          color="green"
          :disabled="!invoiceDate && (!selectedCustomers.length || !carrierQuery)"
          @click="billBulk()"
        >
          {{ `${$t('bill')} ${selectedCustomers.length || totalCustomers} ${$t('customers')}` }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn
          color="red"
          :disabled="!selectedCustomers.length"
          @click="deleteCustomerCorrections()"
        >
          {{ `${$t('delete')} ${$t('customer')} ${$t('corrections')} (${selectedCustomers.length || totalCustomers})` }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row
      class="mb-5"
    >
      <v-col
        cols="2"
      >
        <v-select
          v-model="carrierQuery"
          :items="CARRIERS"
          :label="$t('carrier')"
          item-text="label"
          item-value="slug"
          :loading="loadingCustomers"
        />
      </v-col>
      <v-col cols="2">
        <v-text-field
          v-model="customerNameQuery"
          :label="$t('customer')"
          :loading="loadingCustomers"
        />
      </v-col>
      <v-col cols="2">
        <v-text-field
          :value="totalQuery"
          :label="$t('total')"
          :loading="loadingCustomers"
          @input="debounceInput"
        />
      </v-col>
    </v-row>

    <v-data-table
      :items="customers"
      :options.sync="customerOptions"
      :server-items-length="totalCustomers"
      :loading="loadingCustomers"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100]
      }"
      dense
      class="elevation-1"
    >
      <template v-slot:body="{ items: cs }">
        <v-expansion-panels v-model="activeCustomer">
          <v-expansion-panel
            v-for="(customer,i) in cs"
            :key="i"
            accordion
          >
            <v-expansion-panel-header>
              <v-checkbox
                :value="isSelected(customer)"
                @click.stop="()=>null"
                @change="toggleCustomer(customer)"
              />
              <v-simple-table
                dense
                style="width: 100%;"
                class="mt-4 price-list"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th
                        width="100%"
                        class="display-1"
                      >
                        {{ $t('customer') }}
                      </th>
                      <th class="display-1">
                        {{ $t('customer_id') }}
                      </th>
                      <th class="display-1">
                        {{ $t('count') }}
                      </th>
                      <th class="display-1">
                        {{ $t('total') }}
                      </th>
                      <th class="display-1">
                        {{ $t('cost') }}
                      </th>
                      <th class="display-1">
                        {{ $t('contribution_margin') }}
                      </th>
                      <th class="display-1">
                        {{ $t('currency') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ customer.customer_name }}</td>
                      <td>{{ customer.customer_id }}</td>
                      <td>{{ customer.count }}</td>
                      <td>{{ numberFormat(customer.total) }}</td>
                      <td>{{ numberFormat(customer.total_cost) }}</td>
                      <td style="text-align: center">
                        {{ numberFormat((customer.contribution_margin/customer.total)*100) }}%
                      </td>
                      <td>{{ customer.currency }}</td>
                      <td>
                        <v-btn
                          small
                          color="red"
                          @click="deleteCustomerCorrections(customer.delete_url, customer.customer_name)"
                        >
                          {{ $t('delete') }}
                        </v-btn>
                        <v-btn
                          small
                          class="ml-2"
                          color="green"
                          :disabled="!invoiceDate"

                          @click="billCustomer(customer.invoice_url, customer.customer_name, customer.currency)"
                        >
                          {{ $t('bill') }}
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                v-if="activeCustomer === i"
                :headers="headers"
                :items="items"
                :options.sync="options"
                :server-items-length="totalItems"
                :loading="loading"
                :footer-props="{
                  itemsPerPageOptions: [10, 20, 50, 100]
                }"
                dense
                class="elevation-1"
              >
                <template v-slot:body.prepend>
                  <filter-row
                    :filter-options="filterOptions"
                    :headers="headers"
                    :filter="filter"
                    @set-filter="setFilter"
                  />
                </template>
                <template v-slot:item.earnings="{ item }">
                  <v-icon
                    v-if="(item.sales_price - item.cost) < 0"
                    color="red"
                  >
                    not_interested
                  </v-icon>
                  <v-icon
                    v-else
                    color="green"
                  >
                    check
                  </v-icon>
                </template>
                <template v-slot:item.title="{ item }">
                  <v-chip
                    :color="'green'"
                    text-color="white"
                    small
                  >
                    {{ item.title }}
                  </v-chip>
                </template>
                <template v-slot:item.description="{ item }">
                  <v-chip
                    :color="'green'"
                    text-color="white"
                    small
                  >
                    {{ item.description }}
                  </v-chip>
                </template>
                <template v-slot:item.package_number="{ item }">
                  <v-chip
                    :color="'orange'"
                    text-color="white"
                    small
                  >
                    {{ item.package_number }}
                  </v-chip>
                </template>
                <template v-slot:item.booked_product="{ item }">
                  <v-chip
                    v-if="item.booked_product"
                    :color="'green'"
                    text-color="white"
                    small
                  >
                    {{ item.booked_product }}
                  </v-chip>
                </template>
                <template v-slot:item.real_product="{ item }">
                  <v-chip
                    v-if="item.real_product"
                    :color="'green'"
                    text-color="white"
                    small
                  >
                    {{ item.real_product }}
                  </v-chip>
                </template>

                <template v-slot:item.booked_price="{ item }">
                  {{ numberFormat(item.booked_price) }}
                </template>
                <template v-slot:item.sales_price="{ item }">
                  {{ numberFormat(item.sales_price) }}
                </template>
                <template v-slot:item.difference="{ item }">
                  {{ numberFormat(item.difference) }}
                </template>
                <template v-slot:item.cost="{ item }">
                  {{ numberFormat(item.cost) }}
                </template>
                <template v-slot:item.action="{ item }">
                  <v-btn
                    class="ml-1 px-1 w-100"
                    small
                    color="red"
                    @click="deleteCorrection(item)"
                  >
                    {{ $t('delete') }}
                  </v-btn>
                  <v-btn
                    class="ml-1 px-1 w-100"
                    small
                    color="orange"
                    @click="showCorrectionLogs(item)"
                  >
                    {{ $t('logs') }}
                  </v-btn>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-data-table>

    <v-dialog
      :value="!!correctionLogs"
      min-width="750"
      @click:outside="correctionLogs = null"
    >
      <v-card>
        <v-card-title
          class="text-h5 grey lighten-2 pb-4"
          primary-title
        >
          {{ $t('correction') + ' ' + $t('log') }}
        </v-card-title>

        <v-card-text>
          <v-simple-table
            dense
            class="mt-4"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    width="100%"
                    class="display-1"
                  >
                    {{ $t('description') }}
                  </th>
                  <th class="display-1">
                    {{ $t('specification_line') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="log in correctionLogs"
                  :key="log.id"
                >
                  <td>{{ log.message }}</td>
                  <td>{{ log.specification_line_id }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="correctionLogs = null"
          >
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </base-material-card>
</template>

<script>
  import _ from 'lodash'
  import axios from 'axios'

  export default {

    data () {
      return {
        datePicker: null,
        dialog: {},
        endpoint: 'specifications/corrections/customers',
        name: 'corrections',
        shortPoll: null,
        filter: {},
        items: [],
        totalItems: 0,
        totalCustomers: 0,
        options: { itemsPerPage: 20, page: 1 },
        customerOptions: { itemsPerPage: 20, page: 1, sortBy: 'id', sortDir: 'desc' },
        loading: false,
        loadingCustomers: false,
        loadingLogs: false,
        cancelToken: null,
        logCancelToken: null,
        customersCancelToken: null,
        invoiceDate: null,
        customers: [],
        headers: [
          {
            sortable: false,
            value: 'earnings',
          },
          {
            sortable: false,
            text: this.$t('id'),
            value: 'id',
          },
          {
            sortable: false,
            text: this.$t('title'),
            value: 'title',
          },
          {
            sortable: false,
            text: this.$t('type'),
            value: 'type',
          },
          {
            sortable: false,
            text: this.$t('description'),
            value: 'description',
          },
          {
            sortable: false,
            text: this.$t('package_number'),
            value: 'package_number',
          },
          {
            sortable: false,
            text: this.$t('booked_product'),
            value: 'booked_product',
          },
          {
            sortable: false,
            text: this.$t('real_product'),
            value: 'real_product',
          },
          {
            sortable: false,
            text: this.$t('erp'),
            value: 'erp_number',
          },
          {
            sortable: true,
            text: this.$t('booked_price'),
            value: 'booked_price',
            align: 'right',
          },
          {
            sortable: true,
            text: this.$t('real_price'),
            value: 'sales_price',
            align: 'right',

          },
          {
            sortable: true,
            text: this.$t('difference'),
            value: 'difference',
            align: 'right',

          },
          {
            sortable: true,
            text: `${this.$t('cost')}`,
            value: 'cost',
            align: 'right',

          },
          {
            sortable: false,
            value: 'action',
          },
        ],
        activeCustomer: null,
        filterOptions: {
          title: {
            label: this.$t('title'),
            type: 'free',
            filter: '',
          },
          package_number: {
            label: this.$t('package_number'),
            type: 'free',
            filter: '',
          },
          type: {
            label: this.$t('type'),
            type: 'free',
            filter: '',
          },
          description: {
            label: this.$t('description'),
            type: 'free',
            filter: '',
          },
          booked_product: {
            label: this.$t('booked_product'),
            type: 'free',
            filter: '',
          },

          real_product: {
            label: this.$t('real_product'),
            type: 'free',
            filter: '',
          },
          booked_price: {
            label: this.$t('booked_price'),
            type: 'free',
            filter: '',
          },
          sales_price: {
            label: this.$t('real_price'),
            type: 'free',
            filter: '',
          },
          difference: {
            label: this.$t('difference'),
            type: 'free',
            filter: '',
          },
          cost: {
            label: this.$t('cost'),
            type: 'free',
            filter: '',
          },
          erp_number: {
            label: this.$t('erp'),
            type: 'free',
            filter: '',
          },
          currency: {
            label: this.$t('currency'),
            boxOnly: true,
            type: 'free',
            filter: '',
          },
          correctionLogs: null,
        },
        customerNameQuery: '',
        carrierQuery: [],
        correctionLogs: null,
        selectedCustomers: [],
        totalQuery: '',
      }
    },
    computed: {
      CARRIERS: function () { return this.$store.state.carriers },
      params: function () {
        return {
          limit: this.options.itemsPerPage,
          page: this.options.page,
          filters: this.filter,
          order_by: this.options.sortBy && this.options.sortBy[0] ? this.options.sortBy[0] : 'id',
          order_dir: this.options.sortDesc ? (this.options.sortDesc[0] === true || this.options.sortDesc[0] === undefined) ? 'desc' : 'asc' : 'desc',

        }
      },
      filters: function () {
        return { company_name: this.customerNameQuery, group: this.carrierQuery, total: this.totalQuery }
      },
      customerParams: function () {
        return {
          page: this.customerOptions.page,
          limit: this.customerOptions.itemsPerPage,
          filters: this.filters,
        }
      },
    },
    watch: {
      activeCustomer: function (newActiveCustomer, oldActiveCustomer) {
        if (newActiveCustomer !== oldActiveCustomer) {
          this.totalItems = 0
          this.items = []
          this.options = { itemsPerPage: 20, page: 1 }
          this.loading = false
        }
        if (newActiveCustomer !== undefined) { this.fetchCorrections() }
      },
      params: {
        handler () {
          if (this.activeCustomer !== null) { this.fetchCorrections() }
        },
        deep: true,
      },
      customerParams: {
        handler () {
          this.getCustomers()
        },
        deep: true,
      },
      filters: function () {
        this.totalItems = 0
        this.items = []
        this.options = { itemsPerPage: 20, page: 1 }
      },
    },
    created: function () {
      this.getCustomers()
    },
    methods: {
      debounceInput: _.debounce(function (e) {
        this.totalQuery = e
      }, 500),
      numberFormat: function (number) {
        return new Intl.NumberFormat('da-DK').format(number)
      },
      transformCustomer: function (customer) {
        return {
          customer_id: customer.customer_id,
          currency: customer.currency,
          debtor_number: customer.debtor_number,
          group: customer.group,
          company: customer.company,
        }
      },
      getSelectedCustomerIndex: function (customer) {
        return this.$_.findIndex(this.selectedCustomers, customer)
      },
      toggleCustomer: function (customer) {
        const transformed = this.transformCustomer(customer)

        const selectedIndex = this.getSelectedCustomerIndex(transformed)
        const selectedCustomers = [...this.selectedCustomers]

        if (selectedIndex >= 0) { selectedCustomers.splice(selectedIndex, 1) } else { selectedCustomers.push(transformed) }

        this.selectedCustomers = selectedCustomers
      },
      isSelected: function (customer) {
        const transformed = this.transformCustomer(customer)
        return this.getSelectedCustomerIndex(transformed) >= 0
      },
      showCorrectionLogs ({ id: correctionId }) {
        if (this.logCancelToken) { this.logCancelToken.cancel('cancelled') }
        this.logCancelToken = axios.CancelToken.source()

        this.loadingLogs = true

        this.$get(`/specifications/logs/${correctionId}`, { limit: 99, cancelToken: this.logCancelToken.token })
          .then(response => {
            this.correctionLogs = response.data.data
            this.loadingLogs = false
          })
          .catch(e => {
            if (e !== 'cancelled') this.loadingLogs = false
          })
      },
      fetchCorrections () {
        if (this.activeCustomer === undefined) { return }

        const viewUrl = this.customers[this.activeCustomer].view_url
        if (this.cancelToken) { this.cancelToken.cancel('cancelled') }
        this.cancelToken = axios.CancelToken.source()

        this.loading = true

        this.$get(viewUrl, { ...this.params, cancelToken: this.cancelToken.token })
          .then(response => {
            this.items = response.data.data
            this.totalItems = response.data.total
            this.loading = false
          })
          .catch(e => {
            if (e !== 'cancelled') this.loading = false
          })
      },
      getCustomers () {
        this.selectedCustomers = []
        this.loadingCustomers = true
        if (this.customersCancelToken) { this.customersCancelToken.cancel('cancelled') }
        this.customersCancelToken = axios.CancelToken.source()

        this.$get(`/${this.endpoint}`, { ...this.customerParams, cancelToken: this.customersCancelToken.token })
          .then(response => {
            this.customers = response.data.response_data.data
            this.totalCustomers = response.data.response_data.total

            this.loadingCustomers = false
          })
          .catch(e => {
            if (e !== 'cancelled') this.loadingCustomers = false
          })
      },
      billCustomer (invoiceUrl, customerName) {
        const confirmText = this.$t('confirm_billing_customer', { name: customerName, date: this.invoiceDate })
        if (confirm(confirmText)) {
          this.loading = true

          this.$put(`${invoiceUrl}&invoice_date=${this.invoiceDate}`, { include: invoiceUrl })
            .then(response => {
              this.getCustomers()
            })
            .catch(e => {
              this.loading = false
            })
        }
      },
      billBulk () {
        const customerName = `${(this.selectedCustomers.length || this.totalCustomers)} ${this.$t('customers').toLowerCase()}`
        const confirmText = this.$t('confirm_billing_customer', { name: customerName, date: this.invoiceDate })
        if (confirm(confirmText)) {
          this.loading = true

          this.$put('/specifications/invoice/', { include: this.selectedCustomers }, { query: { filters: this.filters, invoice_date: this.invoiceDate } })
            .then(response => {
              this.getCustomers()
              this.loading = false
            })
            .catch(e => {
              this.loading = false
            })
        }
      },
      deleteCorrection: function (correction) {
        const confirmText = this.$t('confirm_delete', { type: this.$t('correction').toLowerCase() })
        if (confirm(confirmText)) {
          this.loading = true
          this.$del(`/${this.endpoint}/${correction.id}`).then(response => {
            this.fetchCorrections()
          })
            .catch(e => {
              this.loading = false
            })
        }
      },
      deleteCustomerCorrections: function (deleteUrl, cn) {
        const customerName = cn || `${this.selectedCustomers.length} ${this.$t('customers').toLowerCase()}`

        const confirmText = this.$t('confirm_delete_customer_corrections', { name: customerName })
        if (confirm(confirmText)) {
          this.loadingCustomers = true
          this.$post(deleteUrl || `${this.endpoint}/delete`, { include: deleteUrl ? [] : this.selectedCustomers }).then(response => {
            this.getCustomers()
          })
            .catch(e => {
              this.loadingCustomers = false
            })
        }
      },
      setFilter: function (filter) {
        this.filter = filter
      },
    },

  }
</script>
